import {
	type Asset,
	AssetStatusType,
	type AssetType,
	type AssetTypeFieldValues,
	type AssetTypeFieldValuesAttachment,
	type AssetTypeIdentifier,
	type AssetTypeIdentifierValue,
	type AssetTypeStatus,
	fallbackToEmptyArray,
	fallbackToEmptyObject,
	type IssueAssociation,
	type Project,
	type Stored,
	submittedAtModelSortFn,
} from "@overmap-ai/common"
import {
	restructureSelectorWithArgs,
	selectAssets,
	selectAssetTypeFieldValues,
	selectAssetTypeFieldValuesAttachments,
	selectAssetTypeIdentifierValues,
	selectAssetTypes,
	selectAssetTypeStatuses,
	selectIssueAssociations,
} from "@overmap-ai/core"
import { createSelector } from "@reduxjs/toolkit"

import type { RootSelectorWithArgs } from "../typings"

export const selectAssetsOfProjectAndAssetType = restructureSelectorWithArgs(
	createSelector(
		[
			selectAssets,
			(_, projectId: Project["uuid"]) => projectId,
			(_, __, assetTypeId: AssetType["uuid"]) => assetTypeId,
		],
		(assets, projectId, assetTypeId) => {
			return assets.filter(({ project, asset_type }) => project === projectId && asset_type === assetTypeId)
		},
	),
)

export const selectIssueAssociationsByAsset = createSelector([selectIssueAssociations], (issueAssociations) => {
	const ret: Record<Asset["uuid"], Stored<IssueAssociation>[] | undefined> = {}

	for (const association of issueAssociations) {
		const { asset } = association

		if (!ret[asset]) ret[asset] = []

		ret[asset]!.push(association)
	}

	return fallbackToEmptyObject(ret)
})

export const selectFirstAssetTypeStatusByStatusTypeByAssetType = restructureSelectorWithArgs(
	createSelector(
		[selectAssetTypeStatuses, (_, assetTypeIds: AssetType["uuid"][]) => assetTypeIds],
		(assetTypeStatuses, assetTypeIds) => {
			const assetTypeIdsSet = new Set(assetTypeIds)
			const ret: Record<AssetType["uuid"], Record<AssetStatusType, Stored<AssetTypeStatus> | undefined>> = {}

			for (const assetTypeId of assetTypeIds) {
				ret[assetTypeId] = {
					[AssetStatusType.PLANNED]: undefined,
					[AssetStatusType.ACTIVE]: undefined,
					[AssetStatusType.MAINTENANCE]: undefined,
					[AssetStatusType.INACTIVE]: undefined,
					[AssetStatusType.RETIRED]: undefined,
					[AssetStatusType.DISPOSED]: undefined,
				}
			}

			for (const assetTypeStatus of assetTypeStatuses) {
				const { asset_type, type } = assetTypeStatus
				if (!assetTypeIdsSet.has(assetTypeStatus.asset_type)) continue

				const current = ret[asset_type][type]

				if (!current || current.order >= assetTypeStatus.order) {
					ret[asset_type][type] = assetTypeStatus
				}
			}

			return ret
		},
	),
)

export const selectLatestAssetTypeFieldValuesByAsset = createSelector(
	[selectAssetTypeFieldValues],
	(assetTypeFieldValues) => {
		const ret: Record<Asset["uuid"], Stored<AssetTypeFieldValues> | undefined> = {}

		for (const fieldValues of assetTypeFieldValues) {
			const current = ret[fieldValues.asset]

			if (!current || current.submitted_at < fieldValues.submitted_at) {
				ret[fieldValues.asset] = fieldValues
			}
		}

		return fallbackToEmptyObject(ret)
	},
)

export const selectLatestFieldValuesByAssetOfAssetType = restructureSelectorWithArgs(
	createSelector(
		[selectAssets, selectLatestAssetTypeFieldValuesByAsset, (_, assetTypeId: AssetType["uuid"]) => assetTypeId],
		(allAssets, latestFieldValuesByAsset, assetTypeId) => {
			const ret: Record<Asset["uuid"], Stored<AssetTypeFieldValues> | undefined> = {}

			for (const asset of allAssets) {
				if (asset.asset_type !== assetTypeId) continue

				const fieldValues = latestFieldValuesByAsset[asset.uuid]
				if (fieldValues) ret[asset.uuid] = fieldValues
			}

			return fallbackToEmptyObject(ret)
		},
	),
)

export const selectAttachmentsByAssetTypeFieldValues = createSelector(
	[selectAssetTypeFieldValuesAttachments],
	(assetTypeFieldValuesAttachments) => {
		const ret: Record<AssetTypeFieldValues["uuid"], Stored<AssetTypeFieldValuesAttachment>[] | undefined> = {}

		for (const assetTypeFieldValuesAttachment of assetTypeFieldValuesAttachments) {
			const { field_values } = assetTypeFieldValuesAttachment
			if (!(field_values in ret)) ret[field_values] = []
			ret[field_values]!.push(assetTypeFieldValuesAttachment)
		}

		return fallbackToEmptyObject(ret)
	},
)

export const selectAssetTypesOfProject = restructureSelectorWithArgs(
	createSelector(
		[selectAssets, selectAssetTypes, (_, projectId: Project["uuid"]) => projectId],
		(assets, assetTypes, projectId) => {
			const assetTypeIds = new Set(
				assets.filter(({ project }) => projectId === project).map(({ asset_type }) => asset_type),
			)

			return fallbackToEmptyArray(assetTypes.filter(({ uuid }) => assetTypeIds.has(uuid)))
		},
	),
)

export const selectLatestAssetTypeFieldValuesOfAsset: RootSelectorWithArgs<
	Asset["uuid"],
	Stored<AssetTypeFieldValues> | undefined
> = restructureSelectorWithArgs(
	createSelector(
		[selectAssetTypeFieldValues, (_, assetId: Asset["uuid"]) => assetId],
		(assetTypeFieldValues, assetId) => {
			return assetTypeFieldValues
				.filter(({ asset }) => asset === assetId)
				.toSorted(submittedAtModelSortFn)
				.pop()
		},
	),
)

export const selectAssetTypeIdentifierValuesByAsset = createSelector(
	[selectAssetTypeIdentifierValues],
	(assetTypeIdentifierValues) => {
		const ret: Record<Asset["uuid"], Record<AssetTypeIdentifier["uuid"], Stored<AssetTypeIdentifierValue>>> = {}

		for (const identifierValue of assetTypeIdentifierValues) {
			const { asset, asset_type_identifier } = identifierValue

			if (!ret[asset]) ret[asset] = {}

			const current = ret[asset][asset_type_identifier]

			if (!current || current.submitted_at <= identifierValue.submitted_at) {
				ret[asset][asset_type_identifier] = identifierValue
			}
		}

		return fallbackToEmptyObject(ret)
	},
)

export const selectLatestAssetTypeIdentifierValueOfAsset = restructureSelectorWithArgs(
	createSelector(
		[
			selectAssetTypeIdentifierValues,
			(_, assetId: Asset["uuid"]) => assetId,
			(_, __: Asset["uuid"], assetTypeIdentifierId: AssetTypeIdentifier["uuid"]) => assetTypeIdentifierId,
		],
		(assetTypeIdentifierValues, assetId, assetTypeIdentifierId): Stored<AssetTypeIdentifierValue> | undefined => {
			return assetTypeIdentifierValues
				.filter(
					({ asset, asset_type_identifier }) =>
						asset === assetId && asset_type_identifier === assetTypeIdentifierId,
				)
				.toSorted(submittedAtModelSortFn)
				.pop()
		},
	),
)
